import type { GetRecurringTransactionsQuery } from "@/src/gql/graphql";
import type { RecurringTransaction } from "@/lib/types/recurring-transactions";
import type { CompanyEntity } from "@/lib/types/company";
import type { Contact } from "@/lib/types/database";

type RecurringTransactionsCollection = NonNullable<
  GetRecurringTransactionsQuery["recurring_transactionsCollection"]
>;

export type RecurringTransactionGraphQLEdge = RecurringTransactionsCollection["edges"][number];

export type RecurringTransactionGraphQLNode = RecurringTransactionGraphQLEdge["node"];

import {
  isRecurringSyncStatus,
  type RecurringTransactionSyncStatus,
} from "@/lib/types/entities/recurring-transactions";

export type { RecurringTransactionSyncStatus };

function parseSyncStatus(value: string | null | undefined): RecurringTransactionSyncStatus {
  // Canonical status list (includes dead_letter) — previously this local list
  // omitted dead_letter, silently remapping dead-lettered rows to "pending" in UI.
  if (isRecurringSyncStatus(value)) {
    return value;
  }
  return "pending";
}

/** Minimal contact stub synthesized from denormalized customer_id + contact_name. */
export type RecurringTransactionCustomerRef = Pick<Contact, "id" | "name" | "email">;

/** Minimal company stub synthesized from denormalized company_id + company_name. */
export type RecurringTransactionCompanyRef = Pick<CompanyEntity, "id" | "name">;

export function synthesizeRecurringTransactionCustomer(
  node: RecurringTransactionGraphQLNode
): RecurringTransactionCustomerRef | undefined {
  if (node.customer_id && node.contact_name != null) {
    return {
      id: node.customer_id,
      name: node.contact_name,
      email: "",
    };
  }
  return undefined;
}

export function synthesizeRecurringTransactionCompany(
  node: RecurringTransactionGraphQLNode
): RecurringTransactionCompanyRef | undefined {
  if (node.company_id && node.company_name != null) {
    return {
      id: node.company_id,
      name: node.company_name,
    };
  }
  return undefined;
}

export function mapRecurringTransactionNode(
  node: RecurringTransactionGraphQLNode
): RecurringTransaction {
  const customer = synthesizeRecurringTransactionCustomer(node);
  const company = synthesizeRecurringTransactionCompany(node);

  return {
    id: node.id,
    customer_id: node.customer_id ?? undefined,
    company_id: node.company_id ?? undefined,
    item: node.item,
    description: node.description ?? undefined,
    amount: Number(node.amount),
    quantity: Number(node.quantity),
    sync_status: parseSyncStatus(node.sync_status),
    last_synced_at: node.last_synced_at ?? undefined,
    last_synced_month: node.last_synced_month ?? undefined,
    quickbooks_id: node.quickbooks_id ?? undefined,
    quickbooks_realm_id: node.quickbooks_realm_id ?? undefined,
    created_at: node.created_at ?? new Date().toISOString(),
    updated_at: node.updated_at ?? new Date().toISOString(),
    created_by: node.created_by ?? undefined,
    updated_by: node.updated_by ?? undefined,
    customer: customer as Contact | undefined,
    company: company as CompanyEntity | undefined,
  };
}

export function mapRecurringTransactionEdges(
  edges: RecurringTransactionsCollection["edges"] | undefined | null
): RecurringTransaction[] {
  if (!edges) {
    return [];
  }

  return edges
    .map((edge) => edge?.node)
    .filter((node): node is RecurringTransactionGraphQLNode => node != null)
    .map(mapRecurringTransactionNode);
}
